// What happened at this activation, newest first.
//
// THE READ THE ATTENDEE RECORD SAYS DOES NOT EXIST. `attendees.ts` carries a
// gap called "Recent activity, as one feed" whose text is "there is no
// append-only log of what this service did. The facts are spread across the
// spine, the audit table, the published bundles and the kiosk tables, with
// three different ideas of who an actor is." That sentence is still true about
// one person's history, which is what it is about, and migration 131 makes it
// half false about an activation's: three of those places are now one relation.
// The other half stands, and most of this file is about which half is which.
//
// ---------------------------------------------------------------------------
// THIS FILE DERIVES ALMOST NOTHING
//
// Every value below is a column of `activity_feed`, which is itself a union of
// columns of `spine_live`, `config_bundle` and `report_access`. There is no
// join to `identity`, no second source of truth, and nothing computed from a
// payload that 131 did not already name. The two derivations are `actor`, which
// turns three columns into the phrase the design's fourth column draws, and the
// cursor, which turns two columns into one opaque string and back.
//
// ---------------------------------------------------------------------------
// WHAT IS IN THE FEED AND WHAT IS NOT
//
// Three of the audit's eight sources. 131's header argues each absence at
// length and the short version is that none of the five is a decision taken
// here: 111 gives the report surface nothing at all on either kiosk table, 124
// revokes the rider tokens from the reader role a client viewer runs as, and
// 114's verification rows are swept within about a day.
//
// THE PASSCODE ABSENCE IS THE ONE A READER WILL NOTICE, and it is why
// `cannot_answer` exists on this read rather than only a `subset_note`. The
// design offers three passcode actions in its filter. Two of them are real
// facts this service records and then deletes; the third is not recorded at
// all. A feed that carried the two would answer the same question differently
// depending on when it was asked, and would go quiet behind somebody watching
// it, which is worse than a feed that says it does not carry them. So they are
// named, with the reason, in sentences a screen prints.
//
// ---------------------------------------------------------------------------
// NOBODY IS NAMED TO A CLIENT VIEWER, AND THE MECHANISM IS A PRIVILEGE RATHER
// THAN A SELECT LIST IN THIS FILE
//
// 129 withholds `actor_identity_id`, `actor_claimed`, `actor_session_id` and
// `source_address` from xp_reader at the column level, on the ground that three
// name a Nova person and the fourth says where that person was. 131 keeps them
// out of `activity_feed` entirely and puts the three that answer anything into
// `activity_attribution`, granted to xp_app alone.
//
// So this file reads the second relation only where the access position it
// already resolved is the service's own. That is not defence in depth over the
// grant, it is the only thing that works: a `left join activity_attribution`
// written unconditionally would be refused outright to a client viewer by
// Postgres, with a permission error rather than with nulls, and the screen a
// client is holding would answer 500. exportHistory.ts declines to ask for the
// same values for the same reason and says so in the same place.
//
// The consequence is stated rather than left to be noticed. A client viewer is
// told, in `attribution_note`, that Nova can say which person ran a file and
// that asking is how they find out. A row's `attribution` is a tagged union, so
// "withheld from this reader" and "there was never anybody to name" are
// different values rather than the same null.
//
// ---------------------------------------------------------------------------
// AND NO ATTENDEE IS NAMED TO ANYBODY, WHICH IS THE STRONGER RULE
//
// The design draws an email address in its `detail` column on five of its
// eighteen fixture rows, next to `registration.completed`. 131 refuses to put
// one there and this file does not put one back. Reading who registered at an
// activation is a Nova capability that reports/routes.ts refuses a client
// viewer in words, on a deferred commercial question, and this feed has one row
// per registration: an address in `detail` would hand over that list one row at
// a time through the screen nobody thought to check it against. `entity_id` on
// those rows is the attendee reference, which is a pseudonym scoped to this
// activation that a client viewer can already read out of `spine_live`, and it
// is what the People screen's own url is keyed on.

import { toSafeInteger, utcIso, type Queryable } from "../../api/src/db.js";
import { AttendeeQueryRefused } from "./attendees.js";
import { describeAccess, requireAccess, type AccessPosition } from "./access.js";
import { readListOrder, type ListDir, type ListOrder } from "./listOrder.js";

/**
 * How many recorded acts one page may carry.
 *
 * Sized on the audit rather than on the design's pager, which opens at ten and
 * is a display preference. What these decide is how coarse a `report_access`
 * row is allowed to be, which is the attendee list's reasoning and lands the
 * same way: the rows are the disclosure, so a page is the unit somebody walking
 * an activation's history leaves a record in.
 */
export const ACTIVITY_PAGE_DEFAULT = 50;
export const ACTIVITY_PAGE_MAX = 200;

/**
 * Every action the feed can produce, which is the screen's filter vocabulary.
 *
 * A LIST HERE AND A `case` IN 131, COMPARED BY xp/api/check.mjs, because the two
 * cannot be derived from each other and disagreeing silently is the failure
 * mode. A filter offering an action the view never emits is a control that can
 * only return nothing, which is the People audit's own complaint about
 * `person.withheld`, and a view emitting an action the filter does not offer is
 * a row nobody can narrow to.
 *
 * Seventeen off the spine's `event_type` enum plus `config.published`, which is
 * not an event: nothing in this service emits a publication fact, so the record
 * of one is the bundle row. `export.produced` appears once and is written by
 * two arms, which is 131's reading of the audit's ruling rather than a mistake.
 *
 * NOT `person.withheld` AND NOT `person.blocked`. The design offers both and
 * nothing in this service can ever write either. They are in `cannot_answer`
 * instead, because an option that always returns nothing reads as a broken
 * filter rather than as an absent feature.
 */
export const ACTIVITY_ACTIONS = [
  "registration.started",
  "registration.completed",
  "consent.recorded",
  "consent.withdrawn",
  "attestation.recorded",
  "checkin.recorded",
  "prize_entry.recorded",
  "participation.started",
  "participation.completed",
  "score.submitted",
  "touchpoint.scanned",
  "asset.created",
  "asset.delivery_requested",
  "asset.delivered",
  "station.health_reported",
  "export.produced",
  "event.voided",
  "config.published",
] as const;

/**
 * What kind of thing an action touched.
 *
 * The design's own five words where they fit, and three more for the thirteen
 * event types it never saw. A fixed word per action rather than anything read
 * off a payload: a payload-derived entity would put a free-text value from a
 * device into a filter.
 */
export const ACTIVITY_ENTITIES = [
  "people",
  "consents",
  "attestations",
  "assets",
  "stations",
  "exports",
  "events",
  "activations",
] as const;

export type ActivityAction = (typeof ACTIVITY_ACTIONS)[number];
export type ActivityEntity = (typeof ACTIVITY_ENTITIES)[number];

/**
 * Which of 131's three arms a row came from.
 *
 * ON THE LINE RATHER THAN INFERRED FROM THE ACTION, because of `export.produced`
 * alone. 011 explains why an export writes no spine row, so that arm is empty
 * today, and the audit's ruling is that the interesting case is the two records
 * disagreeing rather than the duplicate. Somebody looking at two rows for one
 * export needs to be able to see which is which without reading this file.
 */
export type ActivitySource = "spine" | "config_bundle" | "report_access";

/**
 * What acted, in the audit's terms.
 *
 * FIVE VALUES WHERE THE DESIGN HAS THREE, and the two extra are both about the
 * difference between a person and a credential.
 *
 *   'station' and 'device' are the spine's own attribution. 001 is right that
 *   there is no person on a capture row: nobody presses a button to make a
 *   registration complete.
 *
 *   'service' is a first-class answer and not a word for unknown. 018 makes
 *   `device_id` null on the two web channels because a browser is not an
 *   enrolled tablet, so such a row was written by this service on somebody's
 *   behalf, which is the design's own "the service means nobody pressed
 *   anything".
 *
 *   'person' is somebody 011 proved, or a name a Nova operator typed into a
 *   publish request. Which of those two it is is in `attribution`.
 *
 *   'credential' is the one the design has no room for and the one 011 cares
 *   most about. XP_ADMIN_TOKEN is a shared secret, so somebody did press a
 *   button and nobody can say who. Calling it 'service' would say nobody
 *   pressed anything and calling it 'person' would claim a name this database
 *   refuses to hold.
 */
export type ActivityActorKind = "station" | "device" | "service" | "person" | "credential";

/**
 * Who acted, where this reader may be told and where anybody could be.
 *
 * A TAGGED UNION RATHER THAN THREE NULLABLE FIELDS, and it is the most important
 * shape in this file. The three states below are not each other, and a null in
 * `actor_identity_id` collapses all of them into "no". `withheld` is this reader
 * not being entitled to a value that exists; `none` is a row that records a
 * machine, where there is no person to be entitled to; `recorded` is the answer.
 * A screen drawing a blank cell for the first would be telling a client that
 * nobody ran their export.
 *
 * The sentence for each state is in `attribution_note` on the page rather than
 * on every line, on exportHistory.ts's reasoning about its own actor column: one
 * sentence under a table beats the same clause repeated down it.
 */
export type ActivityAttribution =
  /** A client viewer. 129's four columns are not theirs, and the page says why. */
  | { kind: "withheld" }
  /** A spine row. There was never a person to name, which is 001's design. */
  | { kind: "none" }
  | {
      kind: "recorded";
      /** The person 011 proved, or null on a credential that names nobody. */
      identity_id: string | null;
      /** What somebody typed where nothing could check it. Never beside a proof. */
      claimed: string | null;
      /** Where the request came from, or null on a row that records no address. */
      source_address: string | null;
    };

/** One recorded act. */
export type ActivityLine = {
  /**
   * 131's key, which is also the cursor.
   *
   * Returned rather than kept private, so that a caller can say which row it is
   * asking about and so that two rows in one clock tick are distinguishable on
   * the screen. It carries its source and its source's own key and nothing else.
   */
  activity_id: string;
  occurred_at: string;
  action: string;
  entity: string;
  /**
   * The attendee reference, the station, the voided event or the file.
   *
   * A pseudonym on the rows that are about a person, never a name and never an
   * address. See the header.
   */
  entity_id: string | null;
  source: ActivitySource;
  /**
   * The server's order, for the rows that have one.
   *
   * Null on everything but the spine, and that null is a fact rather than a gap:
   * there is no cross-source sequence and the audit is explicit that one is not
   * to be invented. Two spine rows in one clock tick are ordered by this; two
   * rows from different arms are ordered arbitrarily and stably, which 131's
   * header sets out.
   */
  spine_seq: number | null;
  actor_kind: ActivityActorKind;
  /** Which side of the engagement, on the rows where there are sides. */
  actor_role: string | null;
  /** How it was proved: a capture channel on the spine, 011's credential elsewhere. */
  actor_credential: string | null;
  /** Which tablet or which station. Never a person, which is the whole of 131's split. */
  actor_ref: string | null;
  /** The three columns above as the one phrase the design's fourth column draws. */
  actor: string;
  attribution: ActivityAttribution;
  /** What it touched, in vocabulary rather than in anything a person typed. */
  detail: string | null;
};

/**
 * Where a page sits, so a caller can ask for the next one without guessing.
 *
 * NO `total` AND NO `offset`, WHICH IS THE DIFFERENCE FROM EVERY OTHER PAGED
 * READ IN THIS PACKAGE and is the point of keying the page.
 *
 * `offset` is absent because it is the bug. This is a union that is appended to
 * while somebody reads it, so a row arriving above the cursor pushes one row
 * past an offset: the reader never sees that one and sees another twice, and
 * neither shows up as an error.
 *
 * `total` is absent because it would cost a second pass over the whole union on
 * every page to produce a number that is out of date before it is drawn, and
 * because `count(*) over ()` in the page statement is the same pass in
 * disguise. The attendee list can afford it over one table with an index on the
 * activation; here it is three arms, one of which is every scan of the show.
 * What replaces it is `more`, which is honest and costs one extra row: the
 * statement asks for one past the page and reports whether it arrived.
 */
export type ActivityPagePosition = {
  limit: number;
  returned: number;
  more: boolean;
  /** The cursor this page was read from, or null for the first page. */
  from: string | null;
  /** The cursor for the next page, or null where this page is the end. */
  next: string | null;
};

/** What the caller narrowed the read to, echoed back so a screen can say so. */
export type ActivityFilters = {
  action: string | null;
  entity: string | null;
};

/**
 * A fact the design wants and this service does not hold.
 *
 * The same shape as `AttendeeGap` and deliberately not the same type. Sharing
 * one would couple two reads that have no reason to agree about anything except
 * the two field names, and the day one of them wants a third field the other
 * would grow it too. `what` is the design's own words so the two can be matched
 * up, and `why` is a sentence a person reads on a screen.
 */
export type ActivityGap = {
  what: string;
  why: string;
};

/**
 * When this feed goes, read off the two facts that would decide it if anything
 * did.
 *
 * IN THE RESPONSE BECAUSE THE DESIGN MAKES A CLAIM HERE AND THE CLAIM IS FALSE.
 * Its footer says "the log is kept for the activation's retention period and
 * then goes with it". Nothing in this service deletes any of it. 126's own
 * column comment says `retention_months` was "RECORDED AND NOT ACTED ON", and
 * the one thing that has since acted on it is 130's purge of unverified
 * addresses, which is a different table. So the honest answer is the agreed
 * period, plus the fact that nothing enforces it here, and it is put in front of
 * whoever is reading rather than left in a migration.
 */
export type ActivityRetention = {
  /** When the show was sealed, or null while its record is still open. */
  sealed_at: string | null;
  /** The client's agreed retention in months, or null where no agreement records one. */
  retention_months: number | null;
  note: string;
};

export type ActivityFeed = {
  activation_id: string;
  as_at: string;
  access: AccessPosition;
  access_note: string;
  events: ActivityLine[];
  page: ActivityPagePosition;
  filters: ActivityFilters;
  retention: ActivityRetention;
  /** What is in the feed and what is not, for whoever reads it as a complete record. */
  subset_note: string;
  /** Why a person is named on some rows and not on others, said once. */
  attribution_note: string;
  cannot_answer: ActivityGap[];
};

export type ActivityOptions = {
  now?: Date;
  limit?: unknown;
  cursor?: unknown;
  action?: unknown;
  entity?: unknown;
  /** One of ACTIVITY_SORT_KEYS, or absent for newest first. */
  sort?: unknown;
  /** asc or desc, and only beside a sort. */
  dir?: unknown;
};

/**
 * The Activity table's columns, each sortable on the server (12 Oct hand-off:
 * every column sorts). When, What happened, Entity, Who acted and What it
 * touched, so "every column sorts" is the length of this array.
 */
export const ACTIVITY_SORT_KEYS = ["when", "action", "entity", "actor", "detail"] as const;
export type ActivitySortKey = (typeof ACTIVITY_SORT_KEYS)[number];
export type ActivityOrder = ListOrder<ActivitySortKey>;

/**
 * The page, resolved before anything is read.
 *
 * Called by the route in front of `beginAccess` so that a malformed request
 * costs no audit row, and called again in here because this is a function that
 * hands over a client's activity and must not assume its arguments have been
 * through a validator it cannot see. Both halves are `resolveAttendeePage`'s
 * reasoning and neither is weaker here.
 *
 * IT THROWS `AttendeeQueryRefused` RATHER THAN A CLASS OF ITS OWN, which
 * `resolveAttemptPage` also does and for the reason its header gives: that class
 * is caught by name in reports/routes.ts and turned into a 400, and a new class
 * would have to be added to that file's imports and to its catch arms, so a
 * route that forgot would answer 500 to a caller who typed a page size wrong.
 * The name is about the shape of the refusal, which is "the caller asked for
 * something this endpoint will not answer", and that is what every refusal below
 * is. The type is not about attendees and the name is by now the weakest thing
 * about it; renaming it is a change to three files and belongs in its own.
 */
export function resolveActivityPage(options: ActivityOptions): ResolvedActivityPage {
  // The order first, because the cursor is read against it.
  const order = readListOrder({ sort: options.sort, dir: options.dir }, ACTIVITY_SORT_KEYS);
  if (!order.ok) throw new AttendeeQueryRefused(order.detail);
  return {
    limit: whole(options.limit, "limit", ACTIVITY_PAGE_DEFAULT, 1, ACTIVITY_PAGE_MAX),
    cursor: parseCursor(options.cursor, order.order),
    action: oneOf(options.action, "action", ACTIVITY_ACTIONS),
    entity: oneOf(options.entity, "entity", ACTIVITY_ENTITIES),
    order: order.order,
  };
}

export type ResolvedActivityPage = {
  limit: number;
  cursor: ActivityCursor | null;
  action: string | null;
  entity: string | null;
  /** Null is the feed's own order, newest first. */
  order: ActivityOrder | null;
};

/**
 * Where the last page stopped: the row it stopped on, which is the whole of
 * 131's sort key, and the order it was walking. The order's own value for that
 * row is not carried; the statement reads it off the row, so a cursor cannot
 * claim a position the row does not hold.
 */
export type ActivityCursor = { occurredAt: string; activityId: string };

function whole(raw: unknown, what: string, fallback: number, least: number, most: number): number {
  if (raw === undefined || raw === null) return fallback;

  const value = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (String(raw).trim() === "" || !Number.isSafeInteger(value)) {
    throw new AttendeeQueryRefused(
      `${what} is ${JSON.stringify(raw)}, which is not a whole number. Omit it for the default of ${fallback}.`,
    );
  }
  if (value < least || value > most) {
    throw new AttendeeQueryRefused(
      `${what} is ${value} and this endpoint answers ${least} to ${most}. ` +
        `A page is what the audit counts in, so there is a ceiling on how much one recorded read may carry.`,
    );
  }
  return value;
}

/**
 * A filter value, refused unless the feed can produce it.
 *
 * REFUSED RATHER THAN ANSWERED WITH NOTHING, which is the decision here and is
 * the opposite of what a search box would do. A filter this feed cannot emit
 * returns an empty page, and an empty page is also what a quiet activation looks
 * like: the reader cannot tell a typo from a fact, and the reading they will
 * take is the reassuring one. So a value outside the vocabulary is a 400 that
 * names the vocabulary, and `cannot_answer` covers the two the design offers
 * that this service can never write.
 */
function oneOf(raw: unknown, what: string, allowed: readonly string[]): string | null {
  if (raw === undefined || raw === null) return null;

  const value = String(raw).trim();
  if (value === "") return null;
  if (!allowed.includes(value)) {
    throw new AttendeeQueryRefused(
      `${what} is ${JSON.stringify(value)}, which this feed never records. ` +
        `It answers one of: ${allowed.join(", ")}. Omit it to read everything.`,
    );
  }
  return value;
}

/**
 * The two halves of 131's sort key, as one string a caller can hold.
 *
 * ONE OPAQUE VALUE RATHER THAN TWO QUERY PARAMETERS, so that a caller cannot
 * assemble half a cursor: a timestamp with somebody else's id is a valid-looking
 * request that silently starts the page in the wrong place, and there is no
 * error for it. The two travel together or not at all.
 *
 * NOT ENCODED AND NOT SIGNED. It is legible on purpose, because a support
 * question about a page that looked wrong is answered by reading the cursor out
 * of a log line, and base64 would buy nothing: the values are already in the
 * response beside it. Nor is it a capability. It selects a position in a list
 * this reader is already entitled to, and the entitlement is decided by
 * `requireAccess` and by row-level security one statement later, so a forged
 * cursor moves somebody around inside their own activation and no further.
 */
export function encodeActivityCursor(
  line: { occurred_at: string; activity_id: string },
  order: ActivityOrder | null = null,
): string {
  // A sorted walk's cursor names its order (12 Oct hand-off: every column
  // sorts, on the server). A position in one ordering carried into another
  // still parses and still returns rows, starting part way down an answer the
  // reader never saw the top of; naming the order makes that a refusal.
  return order === null
    ? `${line.occurred_at} ${line.activity_id}`
    : `${line.occurred_at} ${line.activity_id} ${order.key}.${order.dir}`;
}

function parseCursor(raw: unknown, order: ActivityOrder | null = null): ActivityCursor | null {
  if (raw === undefined || raw === null) return null;

  const value = String(raw).trim();
  if (value === "") return null;

  // On the spaces, because `activity_id` cannot contain one, the timestamp
  // cannot either, and the order tag is a key, a dot and a direction. A cursor
  // with more parts is not a cursor this service wrote.
  const parts = value.split(" ");
  const occurredAt = parts[0] ?? "";
  const activityId = parts[1] ?? "";
  const tag = parts[2] ?? null;

  if (parts.length > 3 || !TIMESTAMP.test(occurredAt) || !ACTIVITY_ID.test(activityId)) {
    throw new AttendeeQueryRefused(
      "that is not a cursor this feed produced. A cursor is the `next` value from the page before it, " +
        "and it means nothing at another activation. Omit it to read from the newest row.",
    );
  }

  const wanted = order === null ? null : `${order.key}.${order.dir}`;
  if (tag !== wanted) {
    throw new AttendeeQueryRefused(
      tag === null
        ? `that cursor was walking the feed newest first, and this read asks for it ordered by ${wanted}. ` +
            "A cursor is a position in one order: omit it to start the new order from its first row."
        : `that cursor was walking the feed ordered by ${tag}, and this read asks for ${wanted ?? "it newest first"}. ` +
            "A cursor is a position in one order: send the sort it came from, or omit it to start again.",
    );
  }

  return { occurredAt, activityId };
}

/** The shape `utcIso` renders, which is what the cursor is built from. */
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/** 131's three prefixes and the keys behind them. Shape-checked, never trusted. */
const ACTIVITY_ID = /^(spine:\d{19}|config:[0-9a-f-]{36}:\d{1,9}|access:[0-9a-f-]{36})$/i;

export async function readActivity(
  c: Queryable,
  activationId: string,
  options: ActivityOptions = {},
): Promise<ActivityFeed> {
  // Resolved first, so a malformed page costs no read of the thing it was
  // refusing to hand over. The export and both attendee reads open the same way.
  const { limit, cursor, action, entity, order } = resolveActivityPage(options);

  // And access before anything is counted, which is the rule every surface in
  // this package opens with. Row-level security answers the wrong reader with
  // fewer rows and no error, so an empty feed and a refused one are the same
  // page unless something makes them different, and on this read the empty
  // answer is the reassuring one.
  //
  // It is also what decides whether the attribution is read at all. The
  // position is a fact about what Postgres will do rather than a claim off the
  // request, which is why it is safe to branch a select list on it.
  const access = await requireAccess(c, activationId);
  const now = options.now ?? new Date();

  const named = access.kind === "service";
  const retention = await readRetention(c, activationId);

  // One past the page, so `more` is a fact rather than a second count. See
  // ActivityPagePosition: the alternative was a pass over the whole union on
  // every page, and the number would be stale before it was drawn.
  const { rows } = await c.query<FeedRow>(feedStatement(named, order), [
    activationId,
    limit + 1,
    cursor?.occurredAt ?? null,
    cursor?.activityId ?? null,
    action,
    entity,
  ]);

  const more = rows.length > limit;
  const events = rows.slice(0, limit).map((row) => lineFrom(row, named));
  const last = events[events.length - 1];

  return {
    activation_id: activationId,
    as_at: now.toISOString(),
    access,
    access_note: describeAccess(access),
    events,
    page: {
      limit,
      returned: events.length,
      more,
      from:
        cursor === null
          ? null
          : encodeActivityCursor({ occurred_at: cursor.occurredAt, activity_id: cursor.activityId }, order),
      // Null on the last page rather than a cursor that would return nothing,
      // so a caller paging until `next` is null cannot loop and a screen has one
      // thing to test rather than two that must agree.
      next: more && last !== undefined ? encodeActivityCursor(last, order) : null,
    },
    filters: { action, entity },
    retention,
    subset_note: SUBSET_NOTE,
    attribution_note: named ? ATTRIBUTION_NAMED : ATTRIBUTION_WITHHELD,
    cannot_answer: [...GAPS],
  };
}

// ------------------------------------------------------------------- the page

type FeedRow = {
  activity_id: string;
  occurred_at: string;
  spine_seq: number | string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  source: string;
  actor_kind: string;
  actor_role: string | null;
  actor_credential: string | null;
  actor_ref: string | null;
  detail: string | null;
  actor_identity_id?: string | null;
  actor_claimed?: string | null;
  source_address?: string | null;
};

/**
 * The columns both audiences may read, and the keyset.
 *
 * ONE STRING BUILT ONCE RATHER THAN ASSEMBLED PER CALL, on the two attendee
 * reads' practice. A statement built from the request is a statement whose text
 * varies by caller, which defeats the plan cache and makes the one thing worth
 * reviewing in this file harder to read.
 *
 * THE KEYSET IS A ROW COMPARISON AND NOT THREE `or`s. `(occurred_at,
 * activity_id) < ($3, $4)` is one expression the planner can use the index for
 * and one a person can check; the disjunctive form is where an off-by-one
 * silently repeats or drops the row on a page boundary. It is a strict `<`, so
 * the row the cursor names is the last row of the previous page and never the
 * first of this one.
 *
 * BOTH CURSOR PARAMETERS ARE TESTED FOR NULL TOGETHER because a row comparison
 * against a null is null rather than true, which would answer a first page with
 * nothing at all: `parseCursor` refuses half a cursor, and this is what makes
 * the absent one mean "from the newest row" rather than "no rows".
 *
 * THE FILTERS ARE `$5 IS NULL OR` AND NOT INTERPOLATED for the same reason the
 * statement is a constant. `oneOf` has already confined both to a closed
 * vocabulary, so this is not about injection; it is that a filter which changes
 * the text of the statement is a filter somebody can get wrong later.
 */
const SELECT = `
  select f.activity_id,
         ${utcIso("f.occurred_at")} as occurred_at,
         f.spine_seq,
         f.action,
         f.entity,
         f.entity_id,
         f.source,
         f.actor_kind,
         f.actor_role,
         f.actor_credential,
         f.actor_ref::text as actor_ref,
         f.detail`;

const FROM = `
    from activity_feed f`;

const WHERE = `
   where f.activation_id = $1
     and ($3::timestamptz is null or $4::text is null
          or (f.occurred_at, f.activity_id) < ($3::timestamptz, $4::text))
     and ($5::text is null or f.action = $5)
     and ($6::text is null or f.entity = $6)
   order by f.occurred_at desc, f.activity_id desc
   limit $2`;

/** For a client viewer. Names `activity_attribution` nowhere, which is the point. */
const LIST_WITHHELD = `${SELECT}${FROM}${WHERE}`;

/**
 * For the service, which may know who.
 *
 * A LEFT JOIN AND NOT AN INNER ONE. Only two of 131's three arms have anything
 * to attribute; the spine has nobody, by 001's design rather than by omission.
 * An inner join would drop every capture row from the feed, which is most of it,
 * and it would do it while looking like a query that returns everything.
 *
 * ON THE ACTIVATION AS WELL AS THE ID, which is redundant and deliberate. 131
 * prefixes every `activity_id` with its source's name and builds it out of that
 * source's own primary key, so the id alone cannot match another activation's
 * row. The second clause costs nothing, lets the planner confine the right side
 * to this activation rather than assembling the union across every client, and
 * means a future arm with a weaker id cannot silently reach across activations.
 */
const LIST_NAMED = `${SELECT},
         a.actor_identity_id::text as actor_identity_id,
         a.actor_claimed,
         a.source_address${FROM}
    left join activity_attribution a
           on a.activity_id   = f.activity_id
          and a.activation_id = f.activation_id${WHERE}`;

// ------------------------------------------------------------ the sorted feed
//
// EVERY COLUMN SORTS, AND THE KEYSET STILL HOLDS (12 Oct hand-off). The feed's
// own order is When, newest first, and the cursor is the row a page stopped on.
// A sorted feed is the same arrangement with the column's value in front: rows
// are ordered by (the value, occurred_at, activity_id), all one way, and the
// next page is the rows past the cursor's row in that same tuple. The cursor
// still names only the row; its value for the column, and its own exact time,
// are read off the row by the statement (131's ids are unique within an
// activation), so a caller cannot hand in a position the row does not hold,
// a cursor naming no row of this activation reads nothing, and a forged one
// still moves somebody around inside their own activation and no further.
//
// MISSING LAST BOTH WAYS. A row comparison against a null is null, so the
// value is compared folded (lower, and '' for missing) behind a flag that puts
// the missing ones after the rest whichever way the column runs: `is null`
// going up, `is not null` going down, sorted the same way as everything else
// in the tuple, which is what lets one `<` or `>` walk it.
//
// THE REQUEST NAMES A KEY AND NEVER A COLUMN. resolveActivityPage has refused
// any key off ACTIVITY_SORT_KEYS; this map is the only place a key becomes
// SQL; and the statements are built from it once per key and direction.

/** What each column's cell prints, as SQL over a row of the feed aliased `x`. */
const ACTIVITY_VALUE: Readonly<Record<Exclude<ActivitySortKey, "when">, (x: string) => string>> = {
  action: (x) => `${x}.action`,
  entity: (x) => `${x}.entity`,
  actor: (x) => actorPhraseSql(x),
  detail: (x) => `${x}.detail`,
};

/**
 * describeActor, in SQL, so Who acted sorts by the phrase the cell prints and
 * not by the four columns behind it. Over the feed's own columns only, which
 * both audiences may read: the withheld attribution is never named, so the
 * order cannot disclose what the column withholds. xp/api/check.mjs holds the
 * two to each other by sorting the feed on this and reading the phrases back.
 */
function actorPhraseSql(x: string): string {
  const who =
    `case when ${x}.actor_role in ('nova_admin', 'nova_editor') then 'Nova' ` +
    `when ${x}.actor_role = 'client_viewer' then 'This client' else 'Somebody' end`;
  return (
    `case ${x}.actor_kind ` +
    `when 'station' then case when ${x}.actor_ref is null then 'A station' else 'Station ' || left(${x}.actor_ref::text, 8) end ` +
    `when 'device' then case when ${x}.actor_ref is null then 'A tablet' else 'Tablet ' || left(${x}.actor_ref::text, 8) end ` +
    `when 'credential' then ${who} || case ${x}.actor_credential when 'admin_token' then ', shared token' ` +
    `when 'trusted_caller' then ', no credential' else ', unrecognised credential' end ` +
    `when 'person' then ${who} || case when ${x}.actor_credential = 'session' then ', signed in' else ', named on the request' end ` +
    `else case ${x}.actor_credential when 'self_serve_web' then 'The service, from a page the person opened' ` +
    `when 'onsite_web' then 'The service, from a page handed over on the stand' else 'The service' end ` +
    `end`
  );
}

/** The tuple a sorted feed is ordered and walked by, over a row aliased `x`. */
function sortColumns(order: ActivityOrder, x: string): string[] {
  if (order.key === "when") return [`${x}.occurred_at`, `${x}.activity_id`];
  const value = ACTIVITY_VALUE[order.key](x);
  const missing = order.dir === "asc" ? `(${value}) is null` : `(${value}) is not null`;
  return [missing, `lower(coalesce(${value}, ''))`, `${x}.occurred_at`, `${x}.activity_id`];
}

function sortedWhere(order: ActivityOrder): string {
  const dir: ListDir = order.dir === "desc" ? "desc" : "asc";
  const past = dir === "desc" ? "<" : ">";
  const columns = sortColumns(order, "f");
  return `
   where f.activation_id = $1
     and ($3::timestamptz is null or $4::text is null
          or (${columns.join(", ")}) ${past}
             (select ${sortColumns(order, "k").join(", ")}
                from activity_feed k
               where k.activation_id = $1
                 and k.activity_id   = $4::text))
     and ($5::text is null or f.action = $5)
     and ($6::text is null or f.entity = $6)
   order by ${columns.map((column) => `${column} ${dir}`).join(", ")}
   limit $2`;
}

const FEED_STATEMENTS = new Map<string, string>();

/** The statement for one audience and one order, built once and then reused. */
function feedStatement(named: boolean, order: ActivityOrder | null): string {
  if (order === null) return named ? LIST_NAMED : LIST_WITHHELD;
  const name = `${named ? "named" : "withheld"}:${order.key}.${order.dir}`;
  const held = FEED_STATEMENTS.get(name);
  if (held !== undefined) return held;
  const where = sortedWhere(order);
  const text = named
    ? `${SELECT},
         a.actor_identity_id::text as actor_identity_id,
         a.actor_claimed,
         a.source_address${FROM}
    left join activity_attribution a
           on a.activity_id   = f.activity_id
          and a.activation_id = f.activation_id${where}`
    : `${SELECT}${FROM}${where}`;
  FEED_STATEMENTS.set(name, text);
  return text;
}

function lineFrom(row: FeedRow, named: boolean): ActivityLine {
  const kind = actorKind(row.actor_kind);

  return {
    activity_id: row.activity_id,
    occurred_at: row.occurred_at,
    action: row.action,
    entity: row.entity,
    entity_id: row.entity_id,
    source: sourceOf(row.source),
    spine_seq: row.spine_seq === null ? null : toSafeInteger(row.spine_seq, "a spine row's sequence"),
    actor_kind: kind,
    actor_role: row.actor_role,
    actor_credential: row.actor_credential,
    actor_ref: row.actor_ref,
    actor: describeActor(kind, row.actor_role, row.actor_credential, row.actor_ref),
    attribution: attributionOf(row, named, kind),
    detail: row.detail,
  };
}

/**
 * The union tag, decided by who read and by what kind of row it is.
 *
 * `none` FOR A SPINE ROW EVEN WHEN THE READER IS NOVA, which is the arm most
 * likely to be read as a bug. 131 gives `activity_attribution` two arms and no
 * spine arm, so the left join finds nothing there and every column comes back
 * null. Reporting that as `recorded` with three nulls would say this service
 * looked for a person and could not find one, when the truth is that there was
 * never a person to find: a capture row records a tablet.
 */
function attributionOf(row: FeedRow, named: boolean, kind: ActivityActorKind): ActivityAttribution {
  if (!named) return { kind: "withheld" };
  if (kind === "station" || kind === "device" || kind === "service") return { kind: "none" };

  return {
    kind: "recorded",
    identity_id: row.actor_identity_id ?? null,
    claimed: row.actor_claimed ?? null,
    source_address: row.source_address ?? null,
  };
}

/**
 * 131's word, checked rather than cast.
 *
 * A value this file does not recognise becomes 'service', which is the answer
 * that claims least: it says nobody pressed anything, which is wrong in a way
 * that under-attributes rather than one that invents an actor. The alternative
 * was throwing, and a feed that refuses to render because one row carries a word
 * added by a later migration is worse than one row reading conservatively.
 */
function actorKind(raw: string): ActivityActorKind {
  return KINDS.includes(raw as ActivityActorKind) ? (raw as ActivityActorKind) : "service";
}

const KINDS: readonly ActivityActorKind[] = ["station", "device", "service", "person", "credential"];

function sourceOf(raw: string): ActivitySource {
  return raw === "spine" || raw === "config_bundle" || raw === "report_access" ? raw : "spine";
}

/**
 * What acted, as the one phrase the design's fourth column draws.
 *
 * A PHRASE AND NEVER A NAME, on exportHistory.ts's reasoning, which holds harder
 * here because this read is served to a client viewer on every row rather than
 * on the export rows alone. Both halves are said where there are two, because
 * the role is which side of the engagement acted and the credential is whether
 * anybody can be held to it, and the second is the one a reader would otherwise
 * assume.
 *
 * SHORT ENOUGH FOR A TABLE CELL. The explanation lives in `attribution_note`,
 * which is one sentence under the table rather than the same clause repeated
 * down it. Do not put an address in here to fill the space: the header has that
 * argument and it is the one rule in this file with a product decision behind it.
 *
 * The station and the device are named by their reference rather than by a
 * label, because there is no label: `station_id` is a uuid and this service
 * holds no station names. The design's "the tablet at station 2" is a phrase it
 * invented. Shortened to eight characters, which is what a person reads off a
 * screen and compares, with the whole value on the line as `actor_ref` for
 * anybody who needs to match it against something.
 */
function describeActor(
  kind: ActivityActorKind,
  role: string | null,
  credential: string | null,
  ref: string | null,
): string {
  if (kind === "station") return ref === null ? "A station" : `Station ${short(ref)}`;
  if (kind === "device") return ref === null ? "A tablet" : `Tablet ${short(ref)}`;

  // The spine's web rows, where 018 refuses to invent a device. The channel is
  // in `actor_credential` and it is the difference between a page an attendee
  // opened themselves and one a crew member handed them, which is worth saying.
  if (kind === "service") {
    if (credential === "self_serve_web") return "The service, from a page the person opened";
    if (credential === "onsite_web") return "The service, from a page handed over on the stand";
    return "The service";
  }

  // An Editor (149) is Nova staff, so a client reading this sees "Nova" for
  // their work exactly as for an Admin's: which Nova person is Nova's record.
  const who =
    role === "nova_admin" || role === "nova_editor" ? "Nova" : role === "client_viewer" ? "This client" : "Somebody";

  if (kind === "credential") {
    // 011's own column comment: a shared secret names nobody, and this is the
    // one value here a reader should act on if it is the third.
    if (credential === "admin_token") return `${who}, shared token`;
    if (credential === "trusted_caller") return `${who}, no credential`;
    return `${who}, unrecognised credential`;
  }

  // A person, which is either a proved sign-in or a typed name. Both read the
  // same in a cell and are told apart in `attribution`, because the difference
  // between them is exactly what 107 renamed a column to stop a query getting
  // wrong.
  return credential === "session" ? `${who}, signed in` : `${who}, named on the request`;
}

/** Enough of a uuid to compare by eye, which is what the cell is for. */
const short = (value: string): string => value.slice(0, 8);

// ------------------------------------------------------------------ the notes

const SUBSET_NOTE =
  "Everything this service recorded against this activation in three of the places it keeps records: " +
  "every capture event from the stands and the web pages, every published configuration version, and " +
  "every file of this activation's people that was asked for. Voided events are shown as voids rather " +
  "than removed, and what they voided is not shown. Nothing here is a name or an address: a row about a " +
  "person carries the reference that activation's own records use. It is not everything that happened. " +
  "The passcodes sent to attendees, the screen tokens and the rider tokens are not in it, and " +
  "cannot_answer says why for each.";

const ATTRIBUTION_NAMED =
  "Who acted is named where this service proved it: a sign-in resolves to a person, a shared admin token " +
  "names nobody and says so, and a publish records whatever name the operator typed, which nothing " +
  "checked. Capture events have no person at all and are attributed to the stand's tablet or to the " +
  "station, which is correct rather than missing: nobody presses a button to make a registration " +
  "complete.";

const ATTRIBUTION_WITHHELD =
  "Which Nova person acted is not shown here, and neither is the address they read from. What is shown is " +
  "the role and how well it was proved, which is the difference between a named Nova person doing this " +
  "and Nova being able to say who, and somebody holding a shared token where nobody can. Nova holds the " +
  "rest against every row on this page, and asking is how it gets looked up. Capture events have no " +
  "person on them for anybody: a tablet recorded them.";

// ------------------------------------------------------------------- the gaps
//
// Frozen and named individually, on attendees.ts's practice, so that a gap that
// gets filled in later is deleted from one place. Every one of these is a thing
// the design draws or offers.

const GAPS: readonly ActivityGap[] = Object.freeze([
  Object.freeze({
    what: "auth.passcode_issued",
    why:
      "Not carried. The verification rows are swept within about a day by design, so a feed that showed " +
      "them would empty itself behind whoever was reading it and would answer the same question " +
      "differently depending on when it was asked. That is worse than a feed that says it does not carry " +
      "them. The rows also hold no address, only a digest of one, so nothing could be shown about who a " +
      "passcode went to.",
  }),
  Object.freeze({
    what: "auth.passcode_used",
    why:
      "Not carried, for the same reason as the issue above: the row that would prove it is deleted the " +
      "moment the mailbox is verified. What survives is the registration it bought, which is in this feed " +
      "as registration.completed, and the moment the address was proved, which is on that person's own " +
      "record.",
  }),
  Object.freeze({
    what: "auth.passcode_expired",
    why:
      "Never recorded anywhere. No row is written when a passcode expires: the moment it was due to stop " +
      "working is on the row as a timestamp and the sweep removes it later, so an expiry is implied by two " +
      "dates rather than recorded as an event. It cannot be backfilled.",
  }),
  Object.freeze({
    what: "person.withheld, and person.blocked",
    why:
      "No such action exists anywhere in this service, so there is nothing to show and nothing to filter " +
      "on. Withdrawing a consent is recorded and the export rule honours it, and that is not the same as " +
      "withholding a named person from every file produced from here on.",
  }),
  Object.freeze({
    what: "kiosk.content_saved, and token.issued",
    why:
      "Not carried. The kiosk tables are readable by the screens themselves and by nothing else on the " +
      "reporting side, and the rider tokens are live credentials for the show that a reporting role is " +
      "deliberately not given. Publishing a screen's content and issuing a token are real acts and are " +
      "recorded; reaching them from here would mean widening a grant that was narrowed on purpose.",
  }),
  Object.freeze({
    what: "Who did it, as an address to search on",
    why:
      "The actor filter matches an email address in the design. This feed carries no address for anybody, " +
      "so there is nothing to match: a capture event was recorded by a tablet, and a Nova person is named " +
      "only to Nova and only as an identity rather than an address. Narrowing by role or by what kind of " +
      "thing acted is what this feed can honestly offer.",
  }),
  Object.freeze({
    what: "The full event record behind a row",
    why:
      "Only the spine carries a payload, and this feed shows one named field of it per kind of event " +
      "rather than the document. The rest of the payload is what a person typed at a stand, so opening it " +
      "would put contact details on a screen that deliberately carries none. A published configuration " +
      "and an export request have no payload at all, so there is nothing further to open on those rows.",
  }),
]);

// -------------------------------------------------------------- the retention

/**
 * The two facts that would decide when this feed goes, if anything decided it.
 *
 * A left join to `client` for the same reason 130's purge uses one: a client
 * that records no retention has no agreed period, and an inner join would answer
 * that case by reporting nothing at all rather than by reporting a null somebody
 * can act on.
 */
async function readRetention(c: Queryable, activationId: string): Promise<ActivityRetention> {
  const { rows } = await c.query<{ sealed_at: string | null; retention_months: number | string | null }>(
    `select ${utcIso("v.sealed_at")} as sealed_at,
            k.retention_months
       from activation v
       left join client k on k.client_id = v.client_id
      where v.activation_id = $1`,
    [activationId],
  );

  const row = rows[0];
  const sealedAt = row?.sealed_at ?? null;
  const months =
    row?.retention_months === undefined || row.retention_months === null
      ? null
      : toSafeInteger(row.retention_months, "the client's agreed retention");

  return { sealed_at: sealedAt, retention_months: months, note: retentionNote(sealedAt, months) };
}

/**
 * What the agreement says, and what actually happens, which are not the same
 * thing here.
 *
 * BOTH SENTENCES EVERY TIME, and the second one is the load-bearing one. 130's
 * note could say when rows go because something removes them; this one cannot,
 * because nothing removes any of these three sources. Saying only the agreed
 * period would repeat the design's footer, which is the specific claim 126's
 * column comment tells the console not to repeat.
 *
 * The sealed case is not a separate branch, unlike 130's. Sealing means the
 * record is final rather than that it is leaving, and for this feed that is the
 * whole difference: a sealed activation's history is exactly the history that
 * must not be deleted, because it is the record of a show whose numbers somebody
 * may still be asked to stand behind.
 */
function retentionNote(sealedAt: string | null, months: number | null): string {
  const agreed =
    months === null
      ? "This client's record names no retention period, so there is no agreed date for any of this to go by."
      : `This client's processing agreement records a retention period of ${months} months.`;

  const sealed =
    sealedAt === null
      ? "This activation is not sealed, so its record is still open and still being added to."
      : "This activation is sealed, so its record is final and nothing further will be added to it.";

  return (
    `${agreed} ${sealed} Nothing in this service deletes any of it: the capture events, the published ` +
    `configurations and the record of every file produced are all kept, and the audit of who read what is ` +
    `kept deliberately, because an audit that forgets is not an audit. So this feed is a standing record ` +
    `rather than one that expires, and the retention period above is what was agreed rather than what is ` +
    `enforced. Deleting on it is unbuilt and is a decision about the whole activation rather than about ` +
    `this screen.`
  );
}
